import nookies from "nookies";
import * as Sentry from "@sentry/nextjs";
import { authFetch, refreshToken } from "./axios";
import {
  REDIRECT_TO_HOME,
  REDIRECT_TO_LOGIN,
} from "./constants/redirect.constant";
import { authApi } from "./services/api";
import { clearCreditTokenOnSignOut } from "./utils/credit-token";

export const authProvider = {
  login: async ({ email, password, ctx }: { email: string; password: string; ctx?: { query?: { to?: string } } }) => {
    try {
      const response = await authFetch("/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: email, password }),
      });

      if (response.ok) {
        const { access_token, refresh_token } = await response.json();

        nookies.set(null, "bearerToken", access_token, {
          maxAge: 40 * 60 * 60,
          path: "/",
          sameSite: "lax", // or 'strict' or 'none'
          secure: process.env.NODE_ENV === "production", // set to true in production
        });

        nookies.set(null, "refreshToken", refresh_token, {
          maxAge: 180 * 24 * 60 * 60,
          path: "/",
          sameSite: "lax", // or 'strict' or 'none'
          secure: process.env.NODE_ENV === "production", // set to true in production
        });

        const redirectUrl = ctx?.query?.to || REDIRECT_TO_HOME;

        return {
          success: true,
          redirectTo: redirectUrl,
        };
      } else {
        const { message } = await response.json();
        return {
          success: false,
          error: {
            name: "LoginError",
            message: message.message || "Invalid username or password",
          },
        };
      }
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: error instanceof Error ? error.message : String(error),
        },
      };
    }
  },

  register: async ({ email, password }: { email: string; password: string }) => {
    try {
      const response = await authApi.authControllerRegister({
        email,
        password,
        passwordConfirm: password,
      });

      // The backend used to answer 201 for failures too, with the reason in
      // the body. It now uses real status codes, but keep reading the body
      // shape as well: an older backend must not be mistaken for a success.
      const body = response.data as unknown as {
        error?: string;
        message?: string;
      };
      if (body?.error) {
        return {
          success: false,
          error: {
            name: "RegistrationError",
            // The REASON, not the generic wrapper. Putting "Email already
            // exists." in `name` and "Registration failed." in `message` is
            // how the customer ended up staring at a message that told them
            // nothing about what to do next.
            message: body.error,
          },
        };
      }

      return { success: true, redirectTo: "/verify-page" };
    } catch (error: unknown) {
      // Axios rejects on 4xx/5xx, so this is now the normal failure path.
      // Prefer the server's sentence over "Request failed with status code
      // 409", which is not something to show a customer.
      const response = (error as {
        response?: { status?: number; data?: { message?: unknown } };
      })?.response;
      const raw = response?.data?.message;
      const serverMessage = Array.isArray(raw)
        ? // class-validator returns an array of messages; the first is the
          // one the customer can act on.
          String(raw[0])
        : typeof raw === "string"
          ? raw
          : null;

      return {
        success: false,
        error: {
          name: "RegistrationError",
          message:
            serverMessage ??
            (error instanceof Error ? error.message : String(error)),
        },
      };
    }
  },

  logout: async () => {
    // Revoke the refresh token server-side BEFORE clearing cookies, so it can no
    // longer be exchanged for access tokens (pairs with backend rotation). The
    // refresh guard reads the REFRESH token from the Authorization header (same
    // as /auth/refresh), so we send that — not the generated client, which would
    // send the access token. Best-effort: a failed revoke must never block the
    // user from logging out locally.
    try {
      const refreshTokenValue = nookies.get(null).refreshToken;
      if (refreshTokenValue) {
        await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${refreshTokenValue}`,
          },
        });
      }
    } catch (error: unknown) {
      console.error("Error revoking refresh token on logout", error);
    }

    try {
      nookies.destroy(null, "bearerToken", { path: "/" }); // Specify path if needed
      nookies.destroy(null, "refreshToken", { path: "/" }); // Specify path if needed
    } catch (error: unknown) {
      console.error("Error destroying cookies", error);
    }
    if (typeof window !== "undefined") {
      Sentry.setUser(null);
    }
    // A credit token held in this browser spends the signed-out customer's
    // money; the next person on it must not find it.
    clearCreditTokenOnSignOut();
    return {
      success: true,
      redirectTo: REDIRECT_TO_LOGIN,
    };
  },

  check: async (ctx?: Parameters<typeof nookies.get>[0]) => {
    const cookies = nookies.get(ctx);
    const bearerToken = cookies.bearerToken;
    if (bearerToken) {
      return {
        authenticated: true,
      };
    } else {
      const refreshed = await refreshToken(ctx);
      if (refreshed) {
        return {
          authenticated: true,
        };
      }
    }
    return {
      authenticated: false,
      logout: true,
      redirectTo: REDIRECT_TO_LOGIN,
    };
  },

  getPermissions: async (ctx?: Parameters<typeof nookies.get>[0]) => {
    const cookies = nookies.get(ctx);
    const auth = cookies.bearerToken;
    if (auth) {
      const response = await authFetch("/user/role");
      const data = await response.json();
      return data.roles;
    }
    return null;
  },

  getIdentity: async (ctx?: Parameters<typeof nookies.get>[0]) => {
    const cookies = nookies.get(ctx);
    const auth = cookies.bearerToken;
    if (auth) {
      const response = await authFetch("/user/me");
      const user = await response.json();
      // Attach the signed-in user to client-side Sentry events so errors are
      // attributable to a person (previously every event showed 0 users).
      // Client-only, so we never mutate a shared server request scope.
      if (typeof window !== "undefined") {
        const u = user as { id?: string | number; email?: string } | null;
        Sentry.setUser(
          u?.id != null ? { id: String(u.id), email: u.email } : null,
        );
      }
      return user;
    }
    if (typeof window !== "undefined") {
      Sentry.setUser(null);
    }
    return null;
  },

  onError: async (error: unknown) => {
    console.error(error);
    return { error };
  },

  forgotPassword: async ({ email }: { email: string }) => {
    const response = await authApi.authControllerRequestReset({
      email,
    });

    if (response.status === 201) {
      alert("Password reset link sent to your email");
      return {
        success: true,
        redirectTo: REDIRECT_TO_LOGIN,
      };
    }

    return {
      success: false,
      error: {
        name: "Forgot Password Error",
        message: "Invalid email or password",
      },
    };
  },
};
